import type {
  ModuleRouteDraftSnapshot,
  ModuleRouteNode,
  ModuleRouteVersionSummary,
  RouteContentDefinition,
  RouteDataSourceBinding,
  RouteDataSourceType,
  RouteGuardDefinition,
  RouteGuardType,
  RouteHttpMethod,
  RouteLayoutKind,
  RouteNodeStatus,
  RoutePortalType,
  RouteRendererKind,
  RouteValidationIssue,
} from '../types/route_management';

export const ROUTE_MANAGEMENT_STORAGE_KEY = 'make.route-management.v1';

export const routePortalTypes: RoutePortalType[] = ['portal', 'adminportal', 'make'];
export const routeLayoutKinds: RouteLayoutKind[] = ['layout.auth', 'layout.public', 'layout.admin'];
export const routeRendererKinds: RouteRendererKind[] = [
  'page.shell',
  'page.grid',
  'page.form',
  'page.detail',
  'page.dashboard',
  'layout.auth',
  'layout.public',
  'layout.admin',
  'widget.markdown',
  'widget.html',
  'widget.metrics',
  'widget.grid',
  'widget.form-section',
];
export const routeNodeStatuses: RouteNodeStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
export const routeGuardTypes: RouteGuardType[] = [
  'auth',
  'workspace',
  'permission',
  'role',
  'feature',
  'tenant',
];
export const routeDataSourceTypes: RouteDataSourceType[] = ['none', 'rest', 'graphql'];
export const routeHttpMethods: RouteHttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const DEFAULT_PORTAL_TYPE: RoutePortalType = 'portal';
const DEFAULT_LAYOUT: RouteLayoutKind = 'layout.auth';
const DEFAULT_KIND: RouteRendererKind = 'page.grid';
const DEFAULT_STATUS: RouteNodeStatus = 'DRAFT';
const DEFAULT_DATASOURCE_TYPE: RouteDataSourceType = 'none';
const DEFAULT_HTTP_METHOD: RouteHttpMethod = 'GET';

const defaultGuards: RouteGuardDefinition[] = [{ type: 'auth' }, { type: 'workspace' }];

export type RoutePathDefinition = {
  portalPrefix: RoutePortalType;
  includeWorkspaceId: boolean;
  includeYear: boolean;
  domain: string | null;
  entity: string | null;
  suffix: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeString = (value: unknown, fallback = '') =>
  typeof value === 'string' ? value : fallback;

const normalizeNullableString = (value: unknown) => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeNumber = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const normalizeBoolean = (value: unknown, fallback = false) =>
  typeof value === 'boolean' ? value : fallback;

const createId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeRoutePortalPrefix = (
  value: unknown,
  fallback: RoutePortalType = DEFAULT_PORTAL_TYPE
): RoutePortalType => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value.trim().replace(/^\/+|\/+$/g, '').replace(/\s+/g, '-');
  return normalized.length > 0 ? normalized : fallback;
};

const isPortalType = (value: unknown): value is RoutePortalType =>
  typeof value === 'string' && normalizeRoutePortalPrefix(value, '').length > 0;

const isLayoutKind = (value: unknown): value is RouteLayoutKind =>
  routeLayoutKinds.includes(value as RouteLayoutKind);

const isRendererKind = (value: unknown): value is RouteRendererKind =>
  routeRendererKinds.includes(value as RouteRendererKind);

const isNodeStatus = (value: unknown): value is RouteNodeStatus =>
  routeNodeStatuses.includes(value as RouteNodeStatus);

const isGuardType = (value: unknown): value is RouteGuardType =>
  routeGuardTypes.includes(value as RouteGuardType);

const isDataSourceType = (value: unknown): value is RouteDataSourceType =>
  routeDataSourceTypes.includes(value as RouteDataSourceType);

const isHttpMethod = (value: unknown): value is RouteHttpMethod =>
  routeHttpMethods.includes(value as RouteHttpMethod);

const slugifyRouteValue = (value: string) => {
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return normalized || 'replace-me';
};

const normalizeRoutePathToken = (value: string, fallback = '') => {
  const normalized = value.trim().replace(/^\/+|\/+$/g, '').replace(/\s+/g, '-');
  return normalized.length > 0 ? normalized : fallback;
};

const normalizeRouteSuffix = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '';
  }

  const normalized = trimmed.replace(/^\/+/, '').replace(/\/{2,}/g, '/');
  return normalized.length > 0 ? `/${normalized}` : '';
};

export const parseRoutePathDefinition = (
  path: string,
  fallbackPortalPrefix: RoutePortalType = DEFAULT_PORTAL_TYPE
): RoutePathDefinition => {
  const segments = path.trim().split('/').filter(Boolean);
  const portalPrefix = normalizeRoutePortalPrefix(segments[0], fallbackPortalPrefix);
  let index = 1;
  let includeWorkspaceId = false;
  let includeYear = false;

  if (segments[index] === ':workspaceId') {
    includeWorkspaceId = true;
    index += 1;
  }

  if (segments[index] === ':year') {
    includeYear = true;
    index += 1;
  }

  const domain = segments[index] ? normalizeRoutePathToken(segments[index]) || null : null;
  const entity = segments[index + 1] ? normalizeRoutePathToken(segments[index + 1]) || null : null;
  const suffixSegments = segments.slice(index + (entity ? 2 : domain ? 1 : 0));

  return {
    portalPrefix,
    includeWorkspaceId,
    includeYear,
    domain,
    entity,
    suffix: normalizeRouteSuffix(suffixSegments.join('/')),
  };
};

export const buildRoutePathFromDefinition = (definition: RoutePathDefinition) => {
  const segments = [normalizeRoutePortalPrefix(definition.portalPrefix)];

  if (definition.includeWorkspaceId) {
    segments.push(':workspaceId');
  }

  if (definition.includeYear) {
    segments.push(':year');
  }

  segments.push(normalizeRoutePathToken(definition.domain ?? '', 'domain'));
  segments.push(normalizeRoutePathToken(definition.entity ?? '', 'replace-me'));

  return `/${segments.join('/')}${normalizeRouteSuffix(definition.suffix)}`;
};

export const buildRoutePathFromRoute = (
  route: Pick<ModuleRouteNode, 'portalType' | 'path' | 'domain' | 'entity'>,
  overrides: Partial<RoutePathDefinition> = {}
) => {
  const currentDefinition = parseRoutePathDefinition(route.path, route.portalType);

  return buildRoutePathFromDefinition({
    portalPrefix:
      overrides.portalPrefix ??
      normalizeRoutePortalPrefix(route.portalType, currentDefinition.portalPrefix),
    includeWorkspaceId: overrides.includeWorkspaceId ?? currentDefinition.includeWorkspaceId,
    includeYear: overrides.includeYear ?? currentDefinition.includeYear,
    domain:
      overrides.domain ??
      normalizeNullableString(route.domain) ??
      currentDefinition.domain ??
      'domain',
    entity:
      overrides.entity ??
      normalizeNullableString(route.entity) ??
      currentDefinition.entity ??
      'replace-me',
    suffix: overrides.suffix ?? currentDefinition.suffix,
  });
};

export const buildDefaultRoutePath = (
  portalType: RoutePortalType,
  seed = 'replace-me',
  domain = 'domain'
) => {
  const entity = slugifyRouteValue(seed);
  return buildRoutePathFromDefinition({
    portalPrefix: portalType,
    includeWorkspaceId: true,
    includeYear: false,
    domain,
    entity,
    suffix: '',
  });
};

export const replaceRoutePortalPrefix = (path: string, portalType: RoutePortalType) => {
  const trimmed = path.trim();
  if (trimmed.length === 0) {
    return buildDefaultRoutePath(portalType);
  }

  const segments = trimmed.replace(/^\/+/, '').split('/');
  if (segments.length === 0) {
    return buildDefaultRoutePath(portalType);
  }

  segments[0] = normalizeRoutePortalPrefix(portalType);
  return `/${segments.join('/')}`;
};

export const createRouteGuard = (type: RouteGuardType = 'auth'): RouteGuardDefinition => ({ type });

const normalizeRouteGuard = (value: unknown): RouteGuardDefinition => {
  const record = isRecord(value) ? value : {};
  return {
    type: isGuardType(record.type) ? record.type : 'auth',
    permission: normalizeNullableString(record.permission),
    role: normalizeNullableString(record.role),
    feature: normalizeNullableString(record.feature),
    tenantScope: normalizeNullableString(record.tenantScope),
  };
};

const normalizeRouteDataSource = (value: unknown): RouteDataSourceBinding | null => {
  if (!isRecord(value)) {
    return null;
  }

  const type = isDataSourceType(value.type) ? value.type : DEFAULT_DATASOURCE_TYPE;
  if (type === 'none') {
    return null;
  }

  return {
    type,
    resource: normalizeNullableString(value.resource),
    method: isHttpMethod(value.method) ? value.method : DEFAULT_HTTP_METHOD,
    queryKey: normalizeNullableString(value.queryKey),
  };
};

const normalizeRouteContent = (
  kind: RouteRendererKind,
  value: unknown
): RouteContentDefinition => {
  const record = isRecord(value) ? value : {};
  return {
    renderer: isRendererKind(record.renderer) ? record.renderer : kind,
    props: isRecord(record.props) ? record.props : {},
  };
};

const normalizeRouteNodeChildren = (
  moduleCode: string,
  value: unknown
): ModuleRouteNode[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((entry, index) => normalizeRouteNode(moduleCode, entry, 100 + index * 10));
};

export const normalizeRouteNode = (
  moduleCode: string,
  value: unknown,
  defaultOrder = 100
): ModuleRouteNode => {
  const record = isRecord(value) ? value : {};
  const kind = isRendererKind(record.kind) ? record.kind : DEFAULT_KIND;
  const rawPath = normalizeString(record.path, '').trim();
  const rawDomain = normalizeString(record.domain, '').trim();
  const rawEntity = normalizeString(record.entity, '').trim();
  const parsedPath = parseRoutePathDefinition(rawPath, DEFAULT_PORTAL_TYPE);
  const portalType = isPortalType(record.portalType)
    ? normalizeRoutePortalPrefix(record.portalType)
    : parsedPath.portalPrefix;
  const includeWorkspaceIdOverride =
    typeof record.includeWorkspaceId === 'boolean' ? record.includeWorkspaceId : null;
  const includeYearOverride =
    typeof record.includeYear === 'boolean' ? record.includeYear : null;
  let path =
    rawPath.length > 0
      ? rawPath
      : buildDefaultRoutePath(
          portalType,
          rawEntity || normalizeString(record.title, 'New route'),
          rawDomain || 'domain'
        );
  if (includeWorkspaceIdOverride != null || includeYearOverride != null) {
    const definition = parseRoutePathDefinition(path, portalType);
    path = buildRoutePathFromDefinition({
      ...definition,
      portalPrefix: portalType,
      includeWorkspaceId: includeWorkspaceIdOverride ?? definition.includeWorkspaceId,
      includeYear: includeYearOverride ?? definition.includeYear,
      domain: rawDomain || definition.domain || 'domain',
      entity:
        rawEntity ||
        definition.entity ||
        slugifyRouteValue(normalizeString(record.title, 'New route')),
    });
  }
  const resolvedPathDefinition = parseRoutePathDefinition(path, portalType);

  return {
    id: normalizeString(record.id, createId('route')).trim() || createId('route'),
    moduleCode: normalizeString(record.moduleCode, moduleCode).trim() || moduleCode,
    portalType,
    path,
    kind,
    title: normalizeString(record.title, 'New route'),
    navLabel: normalizeString(record.navLabel, 'New route'),
    domain: rawDomain || resolvedPathDefinition.domain || '',
    entity: rawEntity || resolvedPathDefinition.entity || '',
    layout: isLayoutKind(record.layout) ? record.layout : DEFAULT_LAYOUT,
    order: normalizeNumber(record.order, defaultOrder),
    enabled: normalizeBoolean(record.enabled, true),
    showInNavigation: normalizeBoolean(record.showInNavigation, true),
    status: isNodeStatus(record.status) ? record.status : DEFAULT_STATUS,
    guards: Array.isArray(record.guards)
      ? record.guards.map((guard) => normalizeRouteGuard(guard))
      : [...defaultGuards],
    dataSource: normalizeRouteDataSource(record.dataSource),
    content: normalizeRouteContent(kind, record.content),
    metadata: {
      ...(isRecord(record.metadata) ? record.metadata : {}),
      includeWorkspaceId: resolvedPathDefinition.includeWorkspaceId,
      includeYear: resolvedPathDefinition.includeYear,
    },
    children: normalizeRouteNodeChildren(moduleCode, record.children),
  };
};

export const createRouteNode = (
  moduleCode: string,
  overrides: Partial<ModuleRouteNode> = {}
): ModuleRouteNode => {
  const portalType = overrides.portalType ?? DEFAULT_PORTAL_TYPE;
  const kind = overrides.kind ?? DEFAULT_KIND;
  const title = overrides.title ?? 'New route';
  const domain = overrides.domain ?? 'domain';
  const entity = overrides.entity ?? slugifyRouteValue(title);

  return normalizeRouteNode(moduleCode, {
    id: overrides.id ?? createId('route'),
    moduleCode,
    portalType,
    path: overrides.path ?? buildDefaultRoutePath(portalType, entity, domain),
    kind,
    title,
    navLabel: overrides.navLabel ?? title,
    domain,
    entity,
    layout: overrides.layout ?? DEFAULT_LAYOUT,
    order: overrides.order ?? 100,
    enabled: overrides.enabled ?? true,
    showInNavigation: overrides.showInNavigation ?? true,
    status: overrides.status ?? DEFAULT_STATUS,
    guards: overrides.guards ?? [...defaultGuards],
    dataSource: overrides.dataSource ?? null,
    content: overrides.content ?? { renderer: kind, props: {} },
    metadata: overrides.metadata ?? {},
    children: overrides.children ?? [],
  });
};

export const createRouteDraftSnapshot = (
  moduleCode: string,
  overrides: Partial<ModuleRouteDraftSnapshot> = {}
): ModuleRouteDraftSnapshot => ({
  versionId: overrides.versionId ?? null,
  version: overrides.version ?? 1,
  checksum: overrides.checksum ?? null,
  status: overrides.status ?? DEFAULT_STATUS,
  routes: Array.isArray(overrides.routes)
    ? overrides.routes.map((route, index) => normalizeRouteNode(moduleCode, route, 100 + index * 10))
    : [],
  updatedAt: overrides.updatedAt ?? new Date().toISOString(),
  updatedBy: overrides.updatedBy ?? null,
  message: overrides.message ?? null,
});

export const normalizeRouteDraftSnapshot = (
  moduleCode: string,
  value: unknown,
  fallbackStatus: RouteNodeStatus = DEFAULT_STATUS
): ModuleRouteDraftSnapshot => {
  const record = isRecord(value) ? value : {};
  const routes = Array.isArray(record.routes)
    ? record.routes.map((route, index) => normalizeRouteNode(moduleCode, route, 100 + index * 10))
    : [];

  return {
    versionId: normalizeNullableString(record.versionId),
    version: normalizeNumber(record.version, 1),
    checksum: normalizeNullableString(record.checksum),
    status: isNodeStatus(record.status) ? record.status : fallbackStatus,
    routes,
    updatedAt: normalizeString(record.updatedAt, new Date().toISOString()),
    updatedBy: normalizeNullableString(record.updatedBy),
    message: normalizeNullableString(record.message),
  };
};

export const summarizeRouteDraftSnapshot = (
  snapshot: ModuleRouteDraftSnapshot,
  fallbackStatus: RouteNodeStatus = snapshot.status
): ModuleRouteVersionSummary => ({
  id: snapshot.versionId ?? `version-${snapshot.version}`,
  version: snapshot.version,
  status: snapshot.status === 'ARCHIVED' ? fallbackStatus : snapshot.status,
  checksum: snapshot.checksum,
  createdAt: snapshot.updatedAt,
  createdBy: snapshot.updatedBy,
  message: snapshot.message,
});

export const touchRouteDraftSnapshot = (
  moduleCode: string,
  snapshot: ModuleRouteDraftSnapshot
): ModuleRouteDraftSnapshot =>
  normalizeRouteDraftSnapshot(moduleCode, {
    ...snapshot,
    version: snapshot.version + 1,
    updatedAt: new Date().toISOString(),
  }, snapshot.status);

export const flattenRouteNodes = (nodes: ModuleRouteNode[]): ModuleRouteNode[] =>
  nodes.flatMap((node) => [node, ...flattenRouteNodes(node.children)]);

export const countRouteNodes = (nodes: ModuleRouteNode[]) => flattenRouteNodes(nodes).length;

export const findRouteNodeById = (
  nodes: ModuleRouteNode[],
  routeId: string
): ModuleRouteNode | null => {
  for (const node of nodes) {
    if (node.id === routeId) {
      return node;
    }
    const childNode = findRouteNodeById(node.children, routeId);
    if (childNode) {
      return childNode;
    }
  }
  return null;
};

export const updateRouteNodeTree = (
  nodes: ModuleRouteNode[],
  routeId: string,
  updater: (node: ModuleRouteNode) => ModuleRouteNode
): ModuleRouteNode[] =>
  nodes.map((node) => {
    if (node.id === routeId) {
      return updater(node);
    }
    if (node.children.length === 0) {
      return node;
    }
    return { ...node, children: updateRouteNodeTree(node.children, routeId, updater) };
  });

export const removeRouteNodeTree = (
  nodes: ModuleRouteNode[],
  routeId: string
): ModuleRouteNode[] =>
  nodes
    .filter((node) => node.id !== routeId)
    .map((node) => ({
      ...node,
      children: removeRouteNodeTree(node.children, routeId),
    }));

export const cloneRouteNode = (node: ModuleRouteNode): ModuleRouteNode => ({
  ...node,
  id: createId('route'),
  title: `${node.title} Copy`,
  navLabel: node.navLabel ? `${node.navLabel} Copy` : node.navLabel,
  children: node.children.map((child) => cloneRouteNode(child)),
});

export const duplicateRouteNodeTree = (
  nodes: ModuleRouteNode[],
  routeId: string
): ModuleRouteNode[] => {
  let duplicated = false;

  const visit = (items: ModuleRouteNode[]): ModuleRouteNode[] => {
    const nextItems: ModuleRouteNode[] = [];

    items.forEach((item) => {
      if (duplicated) {
        nextItems.push(item);
        return;
      }

      if (item.id === routeId) {
        nextItems.push(item, cloneRouteNode(item));
        duplicated = true;
        return;
      }

      nextItems.push({
        ...item,
        children: visit(item.children),
      });
    });

    return nextItems;
  };

  return visit(nodes);
};

const getNextSiblingOrder = (nodes: ModuleRouteNode[]) => {
  if (nodes.length === 0) {
    return 100;
  }
  return Math.max(...nodes.map((node) => node.order)) + 10;
};

export const addRootRoute = (nodes: ModuleRouteNode[], moduleCode: string) => {
  const nextRoute = createRouteNode(moduleCode, { order: getNextSiblingOrder(nodes) });
  return {
    routeId: nextRoute.id,
    routes: [...nodes, nextRoute],
  };
};

export const addChildRoute = (
  nodes: ModuleRouteNode[],
  parentId: string,
  moduleCode: string
) => {
  let createdRouteId: string | null = null;

  const routes = updateRouteNodeTree(nodes, parentId, (node) => {
    const nextRoute = createRouteNode(moduleCode, {
      portalType: node.portalType,
      order: getNextSiblingOrder(node.children),
      path: `${node.path.replace(/\/$/, '')}/child`,
    });
    createdRouteId = nextRoute.id;
    return {
      ...node,
      children: [...node.children, nextRoute],
    };
  });

  return {
    routeId: createdRouteId,
    routes,
  };
};

const stableSortValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => stableSortValue(entry));
  }
  if (isRecord(value)) {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((accumulator, key) => {
        accumulator[key] = stableSortValue(value[key]);
        return accumulator;
      }, {});
  }
  return value;
};

export const serializeRouteDraftSnapshot = (snapshot: ModuleRouteDraftSnapshot) =>
  JSON.stringify(stableSortValue(snapshot));

const loadStoredDraftMap = (): Record<string, ModuleRouteDraftSnapshot> => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(ROUTE_MANAGEMENT_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, ModuleRouteDraftSnapshot>>((accumulator, entry) => {
      const [moduleCode, value] = entry;
      accumulator[moduleCode] = normalizeRouteDraftSnapshot(moduleCode, value);
      return accumulator;
    }, {});
  } catch {
    return {};
  }
};

const saveStoredDraftMap = (drafts: Record<string, ModuleRouteDraftSnapshot>) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(ROUTE_MANAGEMENT_STORAGE_KEY, JSON.stringify(drafts));
};

export const loadStoredRouteDraft = (moduleCode: string) => {
  const drafts = loadStoredDraftMap();
  return drafts[moduleCode] ?? createRouteDraftSnapshot(moduleCode);
};

export const saveStoredRouteDraft = (
  moduleCode: string,
  snapshot: ModuleRouteDraftSnapshot
) => {
  const drafts = loadStoredDraftMap();
  drafts[moduleCode] = normalizeRouteDraftSnapshot(moduleCode, snapshot, snapshot.status);
  saveStoredDraftMap(drafts);
};

export const clearStoredRouteDraft = (moduleCode: string) => {
  const drafts = loadStoredDraftMap();
  delete drafts[moduleCode];
  saveStoredDraftMap(drafts);
};

const createValidationIssue = (
  code: string,
  message: string,
  severity: RouteValidationIssue['severity'],
  routeId: string | null,
  path: string | null
): RouteValidationIssue => ({
  id: `${code}:${routeId ?? 'none'}:${path ?? 'none'}`,
  code,
  severity,
  message,
  routeId,
  path,
});

export const validateRouteDraftSnapshot = (
  snapshot: ModuleRouteDraftSnapshot
): RouteValidationIssue[] => {
  const issues: RouteValidationIssue[] = [];
  const seenIds = new Set<string>();
  const pathToRouteIds = new Map<string, string[]>();

  const visit = (nodes: ModuleRouteNode[]) => {
    const siblingOrders = new Map<number, string[]>();

    nodes.forEach((node) => {
      if (!siblingOrders.has(node.order)) {
        siblingOrders.set(node.order, []);
      }
      siblingOrders.get(node.order)?.push(node.id);
    });

    siblingOrders.forEach((routeIds, order) => {
      if (routeIds.length > 1) {
        routeIds.forEach((routeId) => {
          const route = nodes.find((node) => node.id === routeId);
          issues.push(
            createValidationIssue(
              'duplicate-sibling-order',
              `Sibling routes share order ${order}. Reordering is ambiguous until each sibling has a unique order.`,
              'warning',
              routeId,
              route?.path ?? null
            )
          );
        });
      }
    });

    nodes.forEach((node) => {
      if (seenIds.has(node.id)) {
        issues.push(
          createValidationIssue(
            'duplicate-route-id',
            `Route id ${node.id} is duplicated in the draft.`,
            'error',
            node.id,
            node.path
          )
        );
      } else if (node.id.trim().length === 0) {
        issues.push(
          createValidationIssue('missing-route-id', 'Route id is required.', 'error', null, node.path)
        );
      } else {
        seenIds.add(node.id);
      }

      if (node.path.trim().length === 0) {
        issues.push(
          createValidationIssue('missing-path', 'Route path is required.', 'error', node.id, null)
        );
      } else {
        const currentPath = node.path.trim();
        const routeIds = pathToRouteIds.get(currentPath) ?? [];
        routeIds.push(node.id);
        pathToRouteIds.set(currentPath, routeIds);

        if (!currentPath.startsWith('/')) {
          issues.push(
            createValidationIssue(
              'invalid-path-prefix',
              'Route paths must start with /.',
              'error',
              node.id,
              node.path
            )
          );
        }

        const normalizedPortalPrefix = normalizeRoutePortalPrefix(node.portalType);
        if (!currentPath.startsWith(`/${normalizedPortalPrefix}`)) {
          issues.push(
            createValidationIssue(
              'portal-prefix-mismatch',
              `Path should start with /${normalizedPortalPrefix} to stay aligned with the managed route grammar.`,
              'warning',
              node.id,
              node.path
            )
          );
        }
      }

      if (node.title.trim().length === 0) {
        issues.push(
          createValidationIssue('missing-title', 'Route title is required.', 'error', node.id, node.path)
        );
      }

      if (node.showInNavigation && node.navLabel.trim().length === 0) {
        issues.push(
          createValidationIssue(
            'missing-nav-label',
            'Navigation-visible routes should define a navigation label.',
            'warning',
            node.id,
            node.path
          )
        );
      }

      if (node.content.renderer !== node.kind) {
        issues.push(
          createValidationIssue(
            'renderer-kind-mismatch',
            'Content renderer and route kind should stay aligned.',
            'warning',
            node.id,
            node.path
          )
        );
      }

      node.guards.forEach((guard) => {
        if (guard.type === 'permission' && !guard.permission) {
          issues.push(
            createValidationIssue(
              'missing-permission-guard-value',
              'Permission guards must define a permission value.',
              'error',
              node.id,
              node.path
            )
          );
        }

        if (guard.type === 'role' && !guard.role) {
          issues.push(
            createValidationIssue(
              'missing-role-guard-value',
              'Role guards must define a role value.',
              'error',
              node.id,
              node.path
            )
          );
        }

        if (guard.type === 'feature' && !guard.feature) {
          issues.push(
            createValidationIssue(
              'missing-feature-guard-value',
              'Feature guards must define a feature value.',
              'error',
              node.id,
              node.path
            )
          );
        }
      });

      visit(node.children);
    });
  };

  visit(snapshot.routes);

  pathToRouteIds.forEach((routeIds, path) => {
    if (routeIds.length <= 1) {
      return;
    }

    routeIds.forEach((routeId) => {
      issues.push(
        createValidationIssue(
          'duplicate-path',
          `Path ${path} is duplicated in the route tree.`,
          'error',
          routeId,
          path
        )
      );
    });
  });

  return issues;
};

export const formatRouteManagementTimestamp = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};
